/**
 * Derived alerts. Ported from `BudgetStore.refreshAlerts()`.
 *
 * These are all *conditions*, recomputed from the current snapshot rather than
 * stored, so an alert disappears the moment the thing it complains about is
 * fixed. The iOS app additionally persists event-based alerts (a pending
 * posted, an amount changed) in its own table; those are local to that client
 * and are not reproduced here.
 */

import { BudgetIndex, budgetItemBalance, uncategorizedTransactions } from './balances';
import { lastDayOfMonth, partsInLedgerZone } from './ledger';
import { formatMoney } from './money';
import { isUnresolvedPending } from './pending';
import { PaycheckPeriod } from './paycheck';
import { BudgetItem, Transaction } from './types';

export type AlertKind =
  | 'overBudget'
  | 'lowBalance'
  | 'empty'
  | 'upcomingBill'
  | 'uncategorized'
  | 'unresolvedPending';

export type AlertSeverity = 'info' | 'warning' | 'critical';

export interface BudgetAlert {
  /** Stable across recomputes, so the UI can keep list identity. */
  key: string;
  kind: AlertKind;
  severity: AlertSeverity;
  title: string;
  message: string;
  budgetItemId?: string;
}

const SEVERITY_RANK: Record<AlertSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

/** How many days until the given day-of-month comes around again. */
function daysUntilDueDay(dueDay: number, now: Date): number {
  const { year, month, day } = partsInLedgerZone(now);
  if (dueDay >= day) return dueDay - day;
  // Already past this month's due day, so it lands next month.
  return lastDayOfMonth(year, month) - day + dueDay;
}

function plural(count: number, singular: string, pluralForm: string): string {
  return count === 1 ? singular : pluralForm;
}

export interface DeriveAlertsInput {
  budgetItems: readonly BudgetItem[];
  transactions: readonly Transaction[];
  index: BudgetIndex;
  period: PaycheckPeriod;
  now?: Date;
  rolloverKickoffDate?: string | null;
}

export function deriveAlerts({
  budgetItems,
  transactions,
  index,
  period,
  now = new Date(),
  rolloverKickoffDate,
}: DeriveAlertsInput): BudgetAlert[] {
  const alerts: BudgetAlert[] = [];
  const activeItems = budgetItems.filter((item) => item.isActive);

  for (const item of activeItems) {
    const balance = budgetItemBalance(item, index, period, rolloverKickoffDate, activeItems);

    if (balance.isOverBudget) {
      alerts.push({
        key: `over_budget:${item.id}`,
        kind: 'overBudget',
        severity: 'critical',
        title: `${item.name} is over budget`,
        // Against `available`, not `budgeted`: a fund carrying money forward is
        // only over once it outspends the carry-in too.
        message: `Over by ${formatMoney(balance.spent - balance.available)}`,
        budgetItemId: item.id,
      });
      continue;
    }

    // Empty and low are the same axis, so only the more urgent one is reported.
    if (balance.isEmpty) {
      alerts.push({
        key: `empty:${item.id}`,
        kind: 'empty',
        severity: 'warning',
        title: `${item.name} is empty`,
        message: `All ${formatMoney(balance.available)} is spent`,
        budgetItemId: item.id,
      });
    } else if (balance.isLowBalance) {
      alerts.push({
        key: `low_balance:${item.id}`,
        kind: 'lowBalance',
        severity: 'warning',
        title: `${item.name} is running low`,
        message: `${formatMoney(balance.remaining)} left of ${formatMoney(balance.available)}`,
        budgetItemId: item.id,
      });
    }
  }

  for (const bill of activeItems) {
    if (bill.kind !== 'bill' || bill.dueDay == null) continue;
    const days = daysUntilDueDay(bill.dueDay, now);
    if (days <= 0 || days > 7) continue;
    alerts.push({
      key: `upcoming_bill:${bill.id}`,
      kind: 'upcomingBill',
      severity: 'warning',
      title: `${bill.name} is due soon`,
      message: `Due in ${days} ${plural(days, 'day', 'days')} · ${formatMoney(bill.perPaycheckAmount)}`,
      budgetItemId: bill.id,
    });
  }

  const uncategorized = uncategorizedTransactions(transactions, index).filter(
    (transaction) => transaction.amount < 0
  );
  if (uncategorized.length > 0) {
    alerts.push({
      key: 'uncategorized',
      kind: 'uncategorized',
      severity: 'info',
      title: `${uncategorized.length} ${plural(uncategorized.length, 'transaction needs', 'transactions need')} a category`,
      message: 'Envelope balances stay wrong until these are sorted',
    });
  }

  const unresolved = transactions.filter(
    (transaction) =>
      transaction.deletedAt == null && isUnresolvedPending(transaction, now)
  );
  if (unresolved.length > 0) {
    alerts.push({
      key: 'unresolved_pending',
      kind: 'unresolvedPending',
      severity: 'warning',
      title: `${unresolved.length} pending ${plural(unresolved.length, 'charge', 'charges')} ${plural(unresolved.length, 'needs', 'need')} a decision`,
      message: 'These never posted, so they may be holding money that already moved',
    });
  }

  return alerts.sort((a, b) => {
    const bySeverity = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    return bySeverity !== 0 ? bySeverity : a.title.localeCompare(b.title);
  });
}
